import { DEFAULT_US_ZIP_CODE } from '@/lib/constants';
import { MapPinIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { FC, useState } from 'react';
import dynamic from 'next/dynamic';

// Dynamically import LocationPopOutModal to avoid SSR issues
const LocationPopOutModal = dynamic(
  () =>
    import(
      '@/components/organisms/LocationPopOutModal/LocationPopOutModal'
    ).then((mod) => mod.LocationPopOutModal),
  {
    ssr: false,
    loading: () => null, // Render nothing while the modal component itself is loading
  }
);

interface ZipCodeLinkProps {
  variant?: 'default' | 'blue';
  zip?: string;
  persistZip?: boolean;
  shouldStayOrigin?: boolean;
  disabled?: boolean;
  locationModalTitle?: string;
  locationModalDescription?: string;
}
const ZipCodeLink: FC<ZipCodeLinkProps> = ({
  variant = 'default',
  zip,
  persistZip = false,
  shouldStayOrigin = false,
  disabled = false,
  locationModalTitle,
  locationModalDescription,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const router = useRouter();
  const zipCode =
    zip || (router.query.location as string) || DEFAULT_US_ZIP_CODE;
  const handleOpenModal = () => {
    if (disabled) return;
    setOpenModal(true);
  };
  const handleClose = () => {
    setOpenModal(false);
  };

  return (
    <>
      <div
        role="button"
        aria-label="set zip"
        className={clsx('flex flex-row items-center gap-xs text-body1Medium', {
          'text-blue-medium': variant === 'blue' && !disabled,
          'cursor-pointer': !disabled,
          'cursor-default text-text-disabled': disabled,
        })}
        onClick={handleOpenModal}
      >
        <div className="flex">
          <MapPinIcon width={24} height={24} />
        </div>
        <span className={variant === 'default' ? 'underline' : ''}>
          {zipCode}
        </span>
      </div>
      <LocationPopOutModal
        open={openModal}
        close={handleClose}
        shouldStayOrigin={shouldStayOrigin}
        initialZipCode={zip}
        persistZip={persistZip}
        title={locationModalTitle}
        description={locationModalDescription}
      />
    </>
  );
};

export default ZipCodeLink;
